import { Component, computed, Input, Output, EventEmitter, signal, inject, ViewChild, ElementRef, AfterViewInit, OnInit, OnChanges, SimpleChanges, HostListener, effect } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { SessionService } from '../core/session.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { DateTimeHelperService } from '../shared/date-time-helper.service';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';

// Interface matching backend PassportComment model
export interface CommentItem {
  commentID: number;
  passportID: number | string;  // Backend returns number, but we may use string locally
  loadNumber?: string;
  username?: string;
  employeeName?: string;
  userRole?: string;  // This is the persona/role from backend
  commentText?: string;  // Backend field name
  message?: string;  // For backward compatibility
  createdOn?: string;  // Backend field name
  createdAt?: string;  // For backward compatibility
  updatedOn?: string | null;
  isReadByCurrentUser?: boolean | null;
  readAt?: string | null;
  // Legacy fields for local/mock data
  userID?: string;
  userName?: string;
  persona?: string;
}

export interface CommentsHeaderData {
  loadNumber?: string;
  scheduledText?: string;
  tags?: string[];
  variety?: string;
  haulier?: string;
  origin?: string;
  destination?: string;
  apnName?: string;
  vendorName?: string;
  vendorNumber?: string;
  bulkerNumber?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isCommentsHeaderData(value: unknown): value is CommentsHeaderData {
  return isRecord(value);
}

@Component({
  selector: 'app-comments',
  standalone: true,
  imports: [CommonModule, FormsModule, DateWithOrdinalPipe, LoadingSpinnerComponent],
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.css'
})
export class CommentsComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() passportId: string | null = null;
  @Input() header: CommentsHeaderData | null = null;
  @Input() title: string = 'Passport Comments';
  @Input() readOnly = false;
  @Output() closed = new EventEmitter<void>();

  comments = signal<CommentItem[]>([]);
  newMessage = signal('');
  isLoading = signal(false);
  isSubmitting = signal(false);
  isOnline = signal<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);
  offlineTipVisible = signal(false);

  readonly sendCommentButtonAriaLabel = computed(() =>
    this.isSubmitting() ? 'Sending comment' : 'Send comment'
  );

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly detailsStore = inject(PassportDetailsStore);
  private readonly commentsNotification = inject(CommentsNotificationService);

  @ViewChild('listBottom') listBottom?: ElementRef<HTMLDivElement>;
  @ViewChild('listContainer') listContainer?: ElementRef<HTMLDivElement>;

  constructor() {
    // Watch for comments changes and auto-scroll when comments are loaded/updated
    effect(() => {
      const currentComments = this.comments();
      const currentPassportId = this.passportId;
      
      // Only auto-scroll if we have comments and a passport ID
      if (currentComments.length > 0 && currentPassportId) {
        // Use setTimeout to ensure DOM is updated
        setTimeout(() => this.scrollToBottom(), 100);
      }
    });
  }

  ngOnInit(): void {
    void (async () => {
      // Support navigation with state from other components
      const state: unknown = history?.state;
      if (isRecord(state)) {
        const passportId = state['passportId'];
        if (!this.passportId && typeof passportId === 'string') {
          this.passportId = passportId;
        }
        const header = state['header'];
        if (!this.header && isCommentsHeaderData(header)) {
          this.header = header;
        }
        if (state['readOnly'] === true) {
          this.readOnly = true;
        }
      }
      await this.loadComments();
    })();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.scrollToBottom(), 0);
    // Add test method to globalThis for debugging
    (globalThis as any).testAddCommentDirect = () => this.testAddCommentDirect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When passportId changes (panel opened for different passport), reload comments and scroll
    if (changes['passportId'] && !changes['passportId'].firstChange && changes['passportId'].currentValue) {
      void (async () => {
        await this.loadComments();
        // Wait for view to update, then scroll
        setTimeout(() => this.scrollToBottom(), 100);
      })();
    }
    
    // Also scroll when passportId is set initially (if component is reused)
    if (changes['passportId']?.currentValue && !changes['passportId']?.previousValue) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }
  
  // Debug method - can be called from browser console
  async testAddCommentDirect(): Promise<void> {
    const testMessage = globalThis.prompt('Enter test comment:');
    if (!testMessage) return;
    
    // Force set a known valid passport ID for testing
    const useTestId = globalThis.confirm('Use test passport ID 1? (Cancel to use current)');
    if (useTestId) {
      this.passportId = '1';
    }
    
    this.newMessage.set(testMessage);
    await this.addComment();
  }

  @HostListener('window:online')
  onWindowOnline(): void { this.isOnline.set(true); }

  @HostListener('window:offline')
  onWindowOffline(): void { this.isOnline.set(false); }

  async loadComments(): Promise<void> {
    const passportId = this.passportId || '';

    const userID = localStorage.getItem('userID');
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    if (!token || !userID || !passportId) {
      console.warn('Missing required data for loading comments:', { hasToken: !!token, hasUserID: !!userID, hasPassportId: !!passportId });
      // Try to load from offline storage
      await this.loadFromOfflineStorage(passportId);
      return;
    }
    
    // Check if offline - load from IndexedDB
    if (!this.isOnline()) {
      await this.loadFromOfflineStorage(passportId);
      return;
    }

    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/comments`;
    const headers = { 'Authorization': `${tokenType} ${token}`, 'Content-Type': 'application/json' };
    const body = {
      PassportId: Number.parseInt(passportId, 10),
      UserId: Number.parseInt(userID, 10)
    };
    
    this.isLoading.set(true);
    try {
      const response = await firstValueFrom(this.http.post<any[]>(apiUrl, body, { headers }));
      const normalized = this.normalizeComments(response || []);
      this.comments.set(normalized);
      this.scrollToBottom();
    } catch (error: any) {
      console.warn('Failed to load comments:', error);
      if (error?.status === 404) {
        // No comments found is not an error, just set empty array
        this.comments.set([]);
      } else {
        // Try loading from offline storage on error
        await this.loadFromOfflineStorage(passportId);
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Load comments from offline storage (IndexedDB)
   */
  private async loadFromOfflineStorage(passportId: string): Promise<void> {
    this.isLoading.set(true);
    try {
      // First check if comments are in detailsStore
      const details = this.detailsStore.get(passportId);
      if (details?.comments && details.comments.length > 0) {
        const normalized = this.normalizeComments(details.comments);
        this.comments.set(normalized);
        this.scrollToBottom();
        return;
      }

      // Otherwise try IndexedDB
      const cachedComments = await this.offlineStorage.getComments(Number.parseInt(passportId, 10));
      if (cachedComments && cachedComments.length > 0) {
        const normalized = this.normalizeComments(cachedComments);
        this.comments.set(normalized);
        this.scrollToBottom();
      } else {
        this.comments.set([]);
      }
    } catch (error) {
      console.warn('Error loading comments from offline storage:', error);
      this.comments.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  async addComment(): Promise<void> {
    if (this.isSubmitting()) {
      return;
    }
    // Early validations
    if (!this.validateCommentInput()) return;
    
    const authData = this.getAuthenticationData();
    if (!authData) return;
    
    const parsedIds = this.parseAndValidateIds(this.passportId || '', authData.userID);
    if (!parsedIds) return;
    
    await this.submitComment(parsedIds, authData, this.newMessage().trim());
  }

  private validateCommentInput(): boolean {
    const message = this.newMessage().trim();
    if (!message) {
      console.warn('Cannot add empty comment');
      return false;
    }
    if (!this.isOnline()) {
      console.warn('Cannot add comment while offline');
      return false;
    }
    if (!this.passportId) {
      console.warn('No passport ID available for adding comment');
      return false;
    }
    return true;
  }

  private getAuthenticationData() {
    const userID = localStorage.getItem('userID');
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    
    if (!token || !userID) {
      console.warn('Missing authentication data:', { hasToken: !!token, hasUserID: !!userID });
      return null;
    }
    
    return { userID, token, tokenType };
  }

  private parseAndValidateIds(passportId: string, userID: string) {
    const parsedPassportId = Number.parseInt(String(passportId), 10);
    const parsedUserId = Number.parseInt(String(userID), 10);
    
    if (Number.isNaN(parsedPassportId) || parsedPassportId <= 0) {
      console.warn('Invalid passport ID after parsing:', passportId, '→', parsedPassportId);
      alert(`Invalid passport ID: ${passportId}`);
      return null;
    }
    
    if (Number.isNaN(parsedUserId) || parsedUserId <= 0) {
      console.warn('Invalid user ID after parsing:', userID, '→', parsedUserId);
      alert(`Invalid user ID: ${userID}`);
      return null;
    }
    
    return { parsedPassportId, parsedUserId };
  }

  private async submitComment(parsedIds: { parsedPassportId: number, parsedUserId: number }, authData: any, message: string): Promise<void> {
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/comments/add`;
    const headers = { 'Authorization': `${authData.tokenType} ${authData.token}`, 'Content-Type': 'application/json' };
    
    const roleID = this.getRoleID();
    
    this.logTimezoneInfo();
    
    const body = { 
      passportID: parsedIds.parsedPassportId,
      commentMessage: message,
      userID: parsedIds.parsedUserId,
      roleID: roleID
    };

    this.isSubmitting.set(true);
    try {
      if (!(await this.verifyPassportExists(parsedIds, authData, headers))) {
        return;
      }

      await firstValueFrom(this.http.post<any>(apiUrl, body, { headers }));
      this.newMessage.set('');
      await this.loadComments();
      // Refresh the notification count after adding a comment
      await this.commentsNotification.refresh();
      // Use longer timeout to ensure DOM is updated and scroll works properly
      setTimeout(() => this.scrollToBottom(), 100);
    } catch (error: unknown) {
      console.warn('Failed to add comment:', error);
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          alert('Authentication failed - please login again');
        } else if (error.status === 500 && error.error && typeof error.error === 'object' && 'message' in error.error) {
          const serverMsg = (error.error as { message: string }).message;
          alert(`Failed to add comment: ${serverMsg}`);
        } else {
          alert(`Failed to add comment. Error: ${error.message || 'Unknown error'}`);
        }
      } else {
        alert('Failed to add comment. Error: Unknown error');
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private logTimezoneInfo(): void {
    // Timezone info logging removed for SonarQube compliance
  }

  private async verifyPassportExists(parsedIds: { parsedPassportId: number, parsedUserId: number }, authData: any, headers: any): Promise<boolean> {
    const verifyUrl = `${environment.apiBaseUrl}/api/v1/passports/comments`;
    const body = {
      PassportId: parsedIds.parsedPassportId,
      UserId: parsedIds.parsedUserId
    };
    try {
      await firstValueFrom(this.http.post<any[]>(verifyUrl, body, { headers }));
      return true;
    } catch (verifyError: any) {
      console.warn('Failed to verify passport exists:', verifyError);
      if (verifyError?.status === 404) {
        alert(`Passport ${parsedIds.parsedPassportId} not found. Please check the passport ID.`);
      }
      return false;
    }
  }

  handleBarTap(event: Event): void {
    if (this.isOnline()) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.offlineTipVisible()) return;
    this.offlineTipVisible.set(true);
    globalThis.setTimeout(() => this.offlineTipVisible.set(false), 3000);
  }

  onClose(): void {
    this.closed.emit();
  }

  goBack(): void {
    // Prefer browser history back behavior for now
    history.back();
  }

  get headerLoadNumber(): string {
    return this.header?.loadNumber || '';
  }

  get headerVariety(): string {
    return this.header?.variety ?? (this.header?.tags?.[0] || '');
  }

  get headerVendorName(): string {
    return this.header?.vendorName || '';
  }

  get headerVendorNumber(): string {
    return this.header?.vendorNumber || '';
  }

  get headerBulkerNumber(): string {
    return this.header?.bulkerNumber || '';
  }

  get isTransloading(): boolean {
    return this.session.isTransloading();
  }

  get isGatehouse(): boolean {
    return this.session.isGatehouse() || (this.header as any)?.isGatehouse === true;
  }

  get isScheduler(): boolean {
    return this.session.isScheduler() || (this.header as any)?.isScheduler === true;
  }

  get isReceiver(): boolean {
    return this.session.isReceiver() || (this.header as any)?.isReceiver === true;
  }

  get isShunting(): boolean {
    return this.session.isShunting() || (this.header as any)?.isShunting === true;
  }

  get isGrower(): boolean {
    return this.session.isGrower();
  }

  /**
   * Get the roleID based on current persona
   * Grower: 2, Transloader: 3, Receiver: 4, Shunting: 5, Scheduler: 6, Gatehouse: 7
   */
  private getRoleID(): number {
    if (this.isGrower) return 2;
    if (this.isTransloading) return 3;
    if (this.isReceiver) return 4;
    if (this.isShunting) return 5;
    if (this.isScheduler) return 6;
    if (this.isGatehouse) return 7;
    
    // Fallback: try to get from localStorage
    const persona = (localStorage.getItem('persona') || '').toLowerCase();
    if (persona.includes('grower') || persona.includes('grow')) return 2;
    if (persona.includes('transload')) return 3;
    if (persona.includes('receiv')) return 4;
    if (persona.includes('shunting') || persona.includes('shunt')) return 5;
    if (persona.includes('schedule')) return 6;
    if (persona.includes('gate')) return 7;
    
    console.warn('Unable to determine roleID from persona, defaulting to 2 (Grower)');
    return 2; // Default fallback
  }

  get headerHaulier(): string {
    return this.header?.haulier ?? (this.header?.tags?.[1] || '');
  }

  get headerScheduled(): string {
    return this.header?.scheduledText || '';
  }

  // Prefer full name for display, building from common identity keys
  private getCurrentUserDisplayName(): string {
    const first = localStorage.getItem('given_name')
      || localStorage.getItem('firstName')
      || localStorage.getItem('userFirstName')
      || '';
    const last = localStorage.getItem('family_name')
      || localStorage.getItem('lastName')
      || localStorage.getItem('userLastName')
      || '';
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;

    const direct = localStorage.getItem('displayName')
      || localStorage.getItem('name')
      || '';
    if (direct) return direct;

    const emailOrUpn = localStorage.getItem('preferred_username')
      || localStorage.getItem('email')
      || localStorage.getItem('upn')
      || localStorage.getItem('unique_name')
      || '';
    if (emailOrUpn) return emailOrUpn;

    return localStorage.getItem('userID') || 'User';
  }

  /**
   * Ensures a timestamp string has a 'Z' suffix to indicate UTC timezone.
   * Backend sends timestamps in UTC but without the 'Z' suffix, causing Angular
   * to interpret them as local time. This method adds the 'Z' if missing.
   */
  private readonly dateTimeHelper = inject(DateTimeHelperService);

  private normalizeComments(items: any[]): CommentItem[] {
    const currentUserId = localStorage.getItem('userID') || '';
    const currentDisplayName = this.getCurrentUserDisplayName();
    const isDigitsOnly = (v: string | undefined): boolean => !!v && /^\d+$/.test(v);
    
    const normalized = items.map((item) => {
      // Map backend PassportComment fields to frontend CommentItem
      const normalized: CommentItem = {
        // Core fields
        commentID: item.commentID || item.CommentID || 0,
        passportID: item.passportID || item.PassportID || this.passportId || '',
        loadNumber: item.loadNumber || item.LoadNumber || '',
        
        // User info - handle various field names
        username: item.username || item.Username || item.userName || '',
        employeeName: item.employeeName || item.EmployeeName || '',
        userRole: item.userRole || item.UserRole || '',
        
        // Comment content - backend uses 'commentText', UI expects 'message'
        message: item.commentText || item.CommentText || item.message || '',
        commentText: item.commentText || item.CommentText || item.message || '',
        
        // Timestamps - backend uses 'createdOn', UI expects 'createdAt'
        // Ensure UTC timestamps have 'Z' suffix for proper timezone conversion
        createdAt: this.dateTimeHelper.ensureUtcTimestamp(item.createdOn || item.CreatedOn || item.createdAt),
        createdOn: this.dateTimeHelper.ensureUtcTimestamp(item.createdOn || item.CreatedOn || item.createdAt),
        updatedOn: item.updatedOn || item.UpdatedOn || null,
        
        // Read status
        isReadByCurrentUser: item.isReadByCurrentUser ?? item.IsReadByCurrentUser ?? null,
        readAt: item.readAt || item.ReadAt || null,
        
        // Legacy/compatibility fields
        userName: item.username || item.Username || item.userName || '',
        persona: item.userRole || item.UserRole || item.persona || '',
        userID: String(item.userID || item.UserID || '')
      };
      
      // Clean up display names
      let employeeName = normalized.employeeName;
      let userName = normalized.userName;
      
      // If backend provided a numeric userName, treat as ID and ignore
      if (isDigitsOnly(userName)) {
        userName = '';
      }
      
      // For current user, ensure we display their full name
      if (!employeeName && normalized.userID && normalized.userID === currentUserId) {
        employeeName = currentDisplayName;
      }
      
      // If still no employeeName, use non-numeric userName as a fallback
      if (!employeeName && userName && !isDigitsOnly(userName)) {
        employeeName = userName;
      }
      
      // If still no employeeName, use username
      if (!employeeName && normalized.username) {
        employeeName = normalized.username;
      }
      
      const updated: CommentItem = { ...normalized, employeeName, userName };
      return updated;
    });

    // Sort comments in ascending order by timestamp (oldest first, newest last)
    return normalized.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.createdOn || 0).getTime();
      const dateB = new Date(b.createdAt || b.createdOn || 0).getTime();
      return dateA - dateB; // Ascending order: oldest first
    });
  }

  private scrollToBottom(): void {
    try {
      // Check if we're in desktop view (window width >= 768px)
      const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
      
      if (isDesktop) {
        // For desktop view, scroll the comments-scroll-area container
        const scrollArea = document.querySelector('.comments-scroll-area');
        if (scrollArea) {
          scrollArea.scrollTo({ 
            top: scrollArea.scrollHeight, 
            behavior: 'smooth' 
          });
          return;
        }
      }
      
      // Fallback for mobile view or if desktop scroll area not found
      if (this.listBottom?.nativeElement) {
        this.listBottom.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else if (this.listContainer?.nativeElement) {
        const el = this.listContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    } catch {
      // Ignore scroll errors - non-critical UI operation
    }
  }
}


